import React, { useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { designSystem, reactHookForm } from '@yola/ws-ui';
import { i18next } from '@yola/ws-sdk';
import getCustomHtmlCodeEditorAnnotations from 'src/js/modules/common/helpers/get-custom-html-code-editor-annotations';
import IntegrationSettingsDialogScaffolding from './integration-settings-dialog-scaffolding';

const { Paragraph, CodeEditorField } = designSystem;
const { useForm } = reactHookForm;
const FIELD_NAME = 'verificationTag';

const defaultValidate = () => null;

const GoogleSearchConsoleSettingsDialog = ({
  captions,
  defaultValue,
  onSubmit,
  onCancel,
  validate = defaultValidate,
}) => {
  const editorRef = useRef(null);

  const { control, setError, clearErrors, getValues } = useForm({
    defaultValues: {
      [FIELD_NAME]: defaultValue,
    },
  });

  const onCodeEditorError = useCallback(
    (error) => {
      if (error) {
        setError(FIELD_NAME, { type: 'manual', message: error });
      } else {
        clearErrors(FIELD_NAME);
      }
    },
    [setError, clearErrors]
  );

  const onValidate = useCallback(
    ({ value, session }) =>
      getCustomHtmlCodeEditorAnnotations({
        value,
        session,
      }),
    []
  );

  const handleSubmit = useCallback(() => {
    if (!editorRef.current) return;

    const errorAnnotation = editorRef.current
      .getSession()
      .getAnnotations()
      .find((annotation) => annotation.type === 'error');

    if (errorAnnotation) {
      const { text, row, column } = errorAnnotation;
      setError(FIELD_NAME, {
        type: 'manual',
        message: `Line ${row + 1}, Column ${column}: ${text}`,
      });
      return;
    }

    const trimmedValue = getValues(FIELD_NAME)?.trim();
    const validationMessage = validate(trimmedValue);

    if (validationMessage) {
      setError(FIELD_NAME, { type: 'manual', message: validationMessage });
      return;
    }

    onSubmit(trimmedValue);
  }, [getValues, validate, onSubmit, setError]);

  const onAfterEditorConfigured = useCallback((editor) => {
    editorRef.current = editor;
  }, []);

  return (
    <IntegrationSettingsDialogScaffolding
      captions={{
        title: i18next.t('{integrationType} settings', { integrationType: captions.name }),
        controlName: captions.control.name,
        notes: captions.notes,
      }}
      onCancel={onCancel}
      onSubmit={handleSubmit}
    >
      <div>
        <CodeEditorField
          name={FIELD_NAME}
          control={control}
          options={{
            showLineNumbers: true,
            tabSize: 2,
            autoScrollEditorIntoView: true,
          }}
          onEditorError={onCodeEditorError}
          onValidate={onValidate}
          onAfterEditorConfigured={onAfterEditorConfigured}
        />
      </div>
      <Paragraph appearance="medium-emphasis" size="small">
        <span
          // eslint-disable-next-line yola/react/no-danger
          dangerouslySetInnerHTML={{ __html: captions.control.description }}
        />
      </Paragraph>
    </IntegrationSettingsDialogScaffolding>
  );
};

GoogleSearchConsoleSettingsDialog.propTypes = {
  defaultValue: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  captions: PropTypes.shape({
    name: PropTypes.string.isRequired,
    control: PropTypes.shape().isRequired,
    notes: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  validate: PropTypes.func,
};

export default GoogleSearchConsoleSettingsDialog;
